import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isURL from 'validator/lib/isURL';

import { getSanitisedUrl } from '@lyearn/core/utils/url';
import { emitter, EventsEnum } from '@lyearn/events';
import { useTranslation } from '@lyearn/i18n';
import {
  CloseOutline,
  Edit2Outline,
  ExternalLinkFill,
  Link2Outline,
  PlusOutline,
} from '@lyearn/icons';
import { useReactNodeView } from '@lyearn/lygo-core';
import { Button, Dialog, Input, Stack, Tooltip, Typography } from '@lyearn/ui';

export interface RedirectLinkValue {
  url: string;
  heading: string;
  buttonTitle: string;
}

const EMPTY_VALUE: RedirectLinkValue = { url: '', heading: '', buttonTitle: '' };

// The URL field renders `https://` as a static prefix, so `draft.url` holds only the part after it.
// Any protocol the author types or pastes is stripped so it can't end up duplicated; `http://` is
// stripped too and saved as `https://`, which validation already required.
const URL_PROTOCOL_REGEX = /^\s*https?:\/\//i;
const stripProtocol = (url: string) => url.replace(URL_PROTOCOL_REGEX, '');

const HEADING_CHARACTER_LIMIT = 60;
const BUTTON_TITLE_CHARACTER_LIMIT = 30;

/**
 * Phone-shaped preview mirroring the consumption-time overlay: the block's own media with the
 * "Discover / heading / button" CTA card floating over the bottom, so authors see what learners get.
 */
function PreviewDevice({
  mediaSrc,
  heading,
  buttonTitle,
}: {
  mediaSrc?: string;
  heading: string;
  buttonTitle: string;
}) {
  const { t } = useTranslation();
  console.log('buttonTitle nimishh', buttonTitle, heading);
  const headingRef = useRef<HTMLElement>(null);
  const [showHeadingTooltip, setShowHeadingTooltip] = useState(false);
  const onHeadingTooltipOpen = useCallback(() => {
    const el = headingRef.current;
    setShowHeadingTooltip(!!el && el.scrollWidth > el.clientWidth);
  }, []);
  const onHeadingTooltipClose = useCallback(() => setShowHeadingTooltip(false), []);
  return (
    <div className="relative w-[300px] min-h-[440px] shrink-0 self-stretch overflow-hidden rounded-24 bg-black">
      {mediaSrc ? (
        // eslint-disable-next-line @next/next/no-img-element -- decorative fixed-size preview, not page content
        <img alt="" className="absolute inset-0 h-full w-full object-cover" src={mediaSrc} />
      ) : null}
      <div className="absolute inset-x-12 bottom-16">
        <Stack
          alignItems="center"
          className="w-full gap-x-12 rounded-16 bg-white px-16 py-12 shadow-[0px_8px_32px_rgba(0,0,0,0.24),0px_2px_8px_rgba(0,0,0,0.12)]"
          direction="row"
          justifyContent="between">
          <Stack className="min-w-0 flex-1" direction="col" gapY={1}>
            <Typography
              className="uppercase tracking-[1.2px] text-text-secondary opacity-60"
              variant="label-caps">
              {t('MicroLearning:Viewer.RedirectDiscoverLabel')}
            </Typography>
            <Typography
              className="truncate font-semibold text-text-primary"
              variant="body-short-03">
              {heading || t('MicroLearning:RedirectLink.HeadingLabel')}
            </Typography>
          </Stack>
          <Button
            className="!rounded-18 !bg-black !px-12 !py-8 !text-sm !font-semibold !text-white max-w-[100px]"
            endIcon={<ExternalLinkFill height={14} width={14} />}
            shape="square"
            size="x-small">
            <Typography className="truncate font-semibold text-white" variant="body-short-03">
              {buttonTitle || t('MicroLearning:RedirectLink.ButtonTitlePlaceholder')}
            </Typography>
          </Button>
        </Stack>
      </div>
    </div>
  );
}

/** Label + input pair used throughout the modal form. */
function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Stack className={className} direction="col" gapY={8}>
      <Typography className="font-semibold text-text-primary" variant="heading-xxs">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

/**
 * Per-slide Redirect Link authoring UI for micro-learning media blocks. Renders an "Add" pill when
 * empty, or a summary row (label + Remove/Edit + heading/url card) once configured, plus a modal to
 * edit it. Persists into the block node's `redirectLink` attribute via `updateAttrs`.
 */
export default function RedirectLinkEditor({ disabled }: { disabled?: boolean }) {
  const { t } = useTranslation();
  const { node, updateAttrs } = useReactNodeView();
  const redirectLink: RedirectLinkValue | null = node?.attrs?.redirectLink ?? null;
  const blockId: string | undefined = node?.attrs?.id;
  // Media shown in the modal preview: videos carry a still `thumbnail`; image blocks use `src`
  // directly (a video's `src` is a stream URL, unusable as an <img>).
  const mediaSrc: string | undefined =
    node?.type?.name === 'video' ? node?.attrs?.thumbnail : node?.attrs?.src;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<RedirectLinkValue>(EMPTY_VALUE);
  // Certificate settings live in app-level state (a separate document from this editor), so we
  // can't reach it via context here — the app broadcasts changes over the shared event bus instead.
  // Seed from the emitter's last known value (not just `false`) so a component that mounts after
  // the one-time broadcast already fired — e.g. on remount, undo/redo, or lazy block mounting —
  // still starts with the correct disabled state instead of missing it.
  const [certificateEnabled, setCertificateEnabled] = useState(
    () => emitter.getLast(EventsEnum.CertificateSettingsUpdated)?.certificateEnabled ?? false,
  );

  const hasRedirectLink = Boolean(redirectLink?.url);

  // Tell app-level state (which can't see this editor's own document) whether this block
  // currently has a redirect link — covers initial load, saves, removes, and collaborator edits.
  useEffect(() => {
    if (!blockId) return;
    emitter.emit(EventsEnum.RedirectLinkUpdated, { blockId, hasRedirectLink });
  }, [blockId, hasRedirectLink]);

  useEffect(() => {
    const handleCertificateSettingsUpdated = ({
      certificateEnabled: enabled,
    }: {
      certificateEnabled: boolean;
    }) => {
      setCertificateEnabled(enabled);
    };
    emitter.on(EventsEnum.CertificateSettingsUpdated, handleCertificateSettingsUpdated);
    return () => {
      emitter.off(EventsEnum.CertificateSettingsUpdated, handleCertificateSettingsUpdated);
    };
  }, []);

  const isDisabled = disabled || certificateEnabled;

  const openModal = useCallback(() => {
    setDraft(
      redirectLink ? { ...redirectLink, url: stripProtocol(redirectLink.url) } : EMPTY_VALUE,
    );
    setOpen(true);
  }, [redirectLink]);

  const closeModal = useCallback(() => setOpen(false), []);

  const onSave = useCallback(() => {
    // Strip again defensively before prepending, so a value seeded from a legacy attribute that
    // still carries its protocol can't produce `https://https://…`.
    const url = stripProtocol(draft.url).trim();
    const trimmed: RedirectLinkValue = {
      url: url ? getSanitisedUrl(url) : '',
      heading: draft.heading.trim(),
      buttonTitle: draft.buttonTitle.trim(),
    };
    updateAttrs({ redirectLink: trimmed.url ? trimmed : null });
    setOpen(false);
  }, [draft, updateAttrs]);

  const onRemove = useCallback(() => {
    updateAttrs({ redirectLink: null });
    setOpen(false);
  }, [updateAttrs]);

  // URL, heading, and button title are all required — computed as a reason string (rather than a
  // plain boolean) so the disabled Save button can tell the author exactly what's missing.
  const saveDisabledReason = useMemo(() => {
    const trimmedUrl = draft.url.trim();
    if (!trimmedUrl) return t('MicroLearning:RedirectLink.SaveDisabledUrlRequired');
    if (!isURL(getSanitisedUrl(trimmedUrl), { protocols: ['https'], require_protocol: true })) {
      return t('MicroLearning:RedirectLink.SaveDisabledUrlInvalid');
    }
    if (!draft.heading.trim()) return t('MicroLearning:RedirectLink.SaveDisabledHeadingRequired');
    if (!draft.buttonTitle.trim()) {
      return t('MicroLearning:RedirectLink.SaveDisabledButtonTitleRequired');
    }
    return undefined;
  }, [draft.url, draft.heading, draft.buttonTitle, t]);
  const isSaveDisabled = Boolean(saveDisabledReason);

  // Tooltip on the summary-card heading should only appear when the text is actually clipped by
  // the `truncate` class, not on every hover. Measured on-demand at hover time (rather than via a
  // resize observer) since the card's width only changes on window resize, which is rare enough
  // that a stale measurement from the last hover is an acceptable tradeoff for the simplicity here.
  const headingRef = useRef<HTMLElement>(null);
  const [showHeadingTooltip, setShowHeadingTooltip] = useState(false);
  const onHeadingTooltipOpen = useCallback(() => {
    const el = headingRef.current;
    setShowHeadingTooltip(!!el && el.scrollWidth > el.clientWidth);
  }, []);
  const onHeadingTooltipClose = useCallback(() => setShowHeadingTooltip(false), []);

  const disabledTooltipTitle = certificateEnabled
    ? t('MicroLearning:RedirectLink.DisabledTooltip')
    : undefined;

  return (
    <>
      {redirectLink ? (
        <Stack className="w-full gap-y-12 border-t border-border pt-16" direction="col">
          <Stack alignItems="center" direction="row" justifyContent="between">
            <Typography color="text-text-secondary" variant="body-short-01">
              {t('MicroLearning:RedirectLink.Title')}
            </Typography>
            <Stack alignItems="center" direction="row" gapX={8}>
              {/* Remove stays enabled even when blocked by a certificate, so a user who ends up
                  in a conflicting state (e.g. legacy content) can always self-resolve it. */}
              <Button
                appearance="secondary"
                disabled={disabled}
                size="small"
                startIcon={<CloseOutline className="h-16 w-16 fill-current" />}
                variant="ghost"
                onClick={onRemove}>
                {t('MicroLearning:RedirectLink.Remove')}
              </Button>
              <Tooltip placement="top" title={disabledTooltipTitle}>
                {/* Disabled elements don't fire hover events, so the Tooltip wraps a plain span
                    around the disabled Button instead of the Button itself. */}
                <span className="inline-block">
                  <Button
                    appearance="secondary"
                    disabled={isDisabled}
                    shape="pill"
                    size="small"
                    startIcon={<Edit2Outline className="h-16 w-16 fill-current" />}
                    variant="outlined"
                    onClick={openModal}>
                    {t('MicroLearning:RedirectLink.Edit')}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack
            alignItems="center"
            className="gap-x-16 rounded-12 bg-bg-tertiary px-16 py-12"
            direction="row"
            justifyContent="between">
            {redirectLink.heading ? (
              <Tooltip
                open={showHeadingTooltip}
                placement="top"
                title={redirectLink.heading}
                onClose={onHeadingTooltipClose}
                onOpen={onHeadingTooltipOpen}>
                <Typography
                  ref={headingRef}
                  className="min-w-0 flex-1 truncate font-semibold text-text-primary"
                  variant="heading-s">
                  {redirectLink.heading}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                className="min-w-0 flex-1 truncate font-semibold text-text-primary"
                variant="heading-s">
                {t('MicroLearning:RedirectLink.NoHeadingSet')}
              </Typography>
            )}
            <Stack alignItems="center" className="min-w-0 max-w-[55%]" direction="row" gapX={8}>
              <Link2Outline className="h-20 w-20 shrink-0" fillColorToken="icon-secondary" />
              <Typography
                className="truncate text-text-secondary"
                title={redirectLink.url}
                variant="body-short-01">
                {redirectLink.url}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack className="w-full pt-16 " direction="row">
          <Tooltip placement="top" title={disabledTooltipTitle}>
            <span className="inline-block">
              <Button
                appearance="secondary"
                disabled={isDisabled}
                shape="pill"
                size="medium"
                startIcon={<PlusOutline className="h-20 w-20 fill-current" />}
                variant="outlined"
                onClick={openModal}>
                {t('MicroLearning:RedirectLink.AddButton')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      )}

      <Dialog classes={{ paper: '!w-[900px] !max-w-[95vw]' }} open={open} onClose={closeModal}>
        <Stack className="w-full gap-x-32 p-32" direction="row">
          <Stack className="min-w-0 flex-1 gap-y-24" direction="col" justifyContent="between">
            <Stack className="gap-y-24" direction="col">
              <Stack direction="col" gapY={4}>
                <Typography className="font-semibold text-text-primary" variant="heading-m">
                  {t('MicroLearning:RedirectLink.Title')}
                </Typography>
                <Typography color="text-text-secondary" variant="body-short-01">
                  {t('MicroLearning:RedirectLink.Description')}
                </Typography>
              </Stack>

              <Stack className="gap-y-16" direction="col">
                <Field label={t('MicroLearning:RedirectLink.UrlLabel')}>
                  <Input
                    placeholder={t('MicroLearning:RedirectLink.UrlPlaceholder')}
                    startAdornment={
                      <Typography color="text-text-placeholder" variant="body-short-02">
                        https://
                      </Typography>
                    }
                    value={draft.url}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, url: stripProtocol(e.target.value) }))
                    }
                  />
                </Field>
                <Stack className="gap-x-16" direction="row">
                  <Field className="flex-1" label={t('MicroLearning:RedirectLink.HeadingLabel')}>
                    <Input
                      characterLimit={HEADING_CHARACTER_LIMIT}
                      placeholder={t('MicroLearning:RedirectLink.HeadingPlaceholder')}
                      value={draft.heading}
                      onChange={(e) => setDraft((d) => ({ ...d, heading: e.target.value }))}
                    />
                  </Field>
                  <Field
                    className="flex-1"
                    label={t('MicroLearning:RedirectLink.ButtonTitleLabel')}>
                    <Input
                      characterLimit={BUTTON_TITLE_CHARACTER_LIMIT}
                      placeholder={t('MicroLearning:RedirectLink.ButtonTitlePlaceholder')}
                      value={draft.buttonTitle}
                      onChange={(e) => setDraft((d) => ({ ...d, buttonTitle: e.target.value }))}
                    />
                  </Field>
                </Stack>
              </Stack>

              {redirectLink ? (
                <Stack direction="row">
                  <Button
                    appearance="secondary"
                    shape="pill"
                    size="medium"
                    startIcon={<CloseOutline className="h-16 w-16 fill-current" />}
                    variant="outlined"
                    onClick={onRemove}>
                    {t('MicroLearning:RedirectLink.RemoveRedirection')}
                  </Button>
                </Stack>
              ) : null}
            </Stack>

            <Stack className="gap-x-8" direction="row">
              <Button
                appearance="secondary"
                className="flex-1"
                shape="pill"
                size="large"
                variant="outlined"
                onClick={closeModal}>
                {t('MicroLearning:RedirectLink.Cancel')}
              </Button>
              <Tooltip placement="top" title={saveDisabledReason}>
                {/* Disabled elements don't fire hover events, so the Tooltip wraps a plain span
                    around the disabled Button instead of the Button itself. */}
                <span className="inline-block flex-1">
                  <Button
                    appearance="primary"
                    className="w-full"
                    disabled={isSaveDisabled}
                    shape="pill"
                    size="large"
                    variant="filled"
                    onClick={onSave}>
                    {t('MicroLearning:RedirectLink.Save')}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <PreviewDevice
            buttonTitle={draft.buttonTitle}
            heading={draft.heading}
            mediaSrc={mediaSrc}
          />
        </Stack>
      </Dialog>
    </>
  );
}
